//core
import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";

//hooks
import { useAffProfile } from "../../../../../store/admin/affiliateProfile/useAffProfile";
import { useCommonAdmin } from "../../../../../store/admin/commonAdmin/useCommonAdmin";

//components
import TagSelect, { ITag, TagSelectVariant } from "../../../../UI/Fields/TagSelect";

interface IAffiliateTagSelectProps {
  affiliateId: string | number;
  platformId: string | number;
  tags: ITag[];
  variant?: TagSelectVariant;
  maxVisibleChips?: number;
  showAddOnHover?: boolean;
  minWidth?: number;
}

const AffiliateTagSelect: React.FC<IAffiliateTagSelectProps> = ({
  affiliateId,
  platformId,
  tags,
  variant,
  maxVisibleChips,
  showAddOnHover,
  minWidth = 150,
}): JSX.Element => {
  const { assignTags, removeTags } = useAffProfile();
  const { getTagsList, createTag } = useCommonAdmin();

  const [selectedTags, setSelectedTags] = useState<ITag[]>(tags);

  useEffect(() => {
    setSelectedTags(tags);
  }, [tags]);

  const handleGetTags = useCallback(async () => {
    const res = await getTagsList({ platformId });
    return res?.payload || [];
  }, [platformId]);

  const handleCreateTag = useCallback(async (name: string, color: string): Promise<ITag | null> => {
    const res = await createTag({ name, platformId, color });
    if (res?.payload?.id) {
      return { id: res.payload.id, name, color: res.payload.color || color };
    }
    return null;
  }, [platformId]);

  const handleAssignTag = useCallback(async (tagIds: number[]) => {
    await assignTags({ affiliateId, tagIds: tagIds });
  }, [affiliateId]);

  const handleRemoveTag = useCallback(async (tagIds: number[]) => {
    await removeTags({ affiliateId, tagIds: tagIds });
  }, [affiliateId]);

  return (
    <Box sx={{ minWidth }}>
      <TagSelect
        value={selectedTags}
        variant={variant}
        maxVisibleChips={maxVisibleChips}
        showAddOnHover={showAddOnHover}
        onChange={setSelectedTags}
        getOptions={handleGetTags}
        onCreateTag={handleCreateTag}
        onAssignTag={handleAssignTag}
        onRemoveTag={handleRemoveTag}
      />
    </Box>
  );
};

export default AffiliateTagSelect;
